import { isValidPromptLine, parseAiPromptList } from './parseAiPromptList';
import type { PromptBadgeVariant } from './promptRecommendationBadge';

export type RankedPromptChoice = {
  text: string;
  tier: PromptBadgeVariant | null;
};

const MOST_TAG = /^\[?\s*MOST\s+RECOMMENDED\s*\]?\s*[:\-–]?\s*/i;
const RECOMMENDED_TAG = /^\[?\s*RECOMMENDED\s*\]?\s*[:\-–]?\s*/i;

function tierFromTags(text: string): {
  tier: PromptBadgeVariant | null;
  text: string;
} {
  if (MOST_TAG.test(text)) {
    return {
      tier: 'most',
      text: text.replace(MOST_TAG, '').trim(),
    };
  }
  if (RECOMMENDED_TAG.test(text)) {
    return {
      tier: 'recommended',
      text: text.replace(RECOMMENDED_TAG, '').trim(),
    };
  }
  return { tier: null, text };
}

/** Parse AI output; AI tags pick most + recommended, with sensible fallbacks. */
export function parseRankedAiPromptList(answer: unknown, limit?: number): RankedPromptChoice[] {
  const raw = parseAiPromptList(answer, limit).map((line) => tierFromTags(line));
  if (!raw.length) return [];

  const mostIdx = Math.max(
    0,
    raw.findIndex((r) => r.tier === 'most')
  );
  const ordered = [...raw];
  const [mostItem] = ordered.splice(mostIdx, 1);
  const recommended: typeof raw = [];
  const rest: typeof raw = [];

  for (const item of ordered) {
    if (item.tier === 'recommended' && recommended.length < 3) {
      recommended.push(item);
    } else {
      rest.push(item);
    }
  }

  while (recommended.length < 3 && rest.length > 0) {
    recommended.push(rest.shift()!);
  }

  const ranked = [
    { text: mostItem.text, tier: 'most' as const },
    ...recommended.map((r) => ({
      text: r.text,
      tier: 'recommended' as const,
    })),
    ...rest.map((r) => ({ text: r.text, tier: null })),
  ].filter((row) => isValidPromptLine(row.text));

  return ranked;
}

export function rankedPromptTexts(choices: RankedPromptChoice[]): string[] {
  return choices.map((c) => c.text);
}
